import { Toast } from 'primereact/toast';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { useCompanyContext } from '../../../context/CompanyContext';
import {
  APTransactionEntryArgs,
  APTransactionEntryForm,
  APTransactionEntryUpdateArgs,
  APTransactionItemType,
} from '../../../Interfaces/Accounting/ACForms.interfaces';
import APTransactionForm from '@/src/app/features/accounting/forms/APTransactionEntry/apTransactionForm';
import { Button } from 'primereact/button';
import LoadingButton from '@/src/app/components/inputs/LoadingButton';
import ConfirmationDialog from '@/src/app/components/messages/ConfirmationDialog';
import { formatUTCDate, roundCurrency } from '@/src/utils/formatUtils';
import { AxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  deleteAPTransactionEntry,
  fetchAPEntryDrafts,
  fetchAPEntryById,
  fetchSubmittedAPEntries,
  postAPTransactionEntry,
  putApproveAPTransactionEntry,
  putAPTransactionEntry,
  putRejectAPTransactionEntry,
  putSubmitAPTransactionEntry,
  putUploadAPTransactionEntry,
} from '@/src/app/services/accounting/ACFormsService';
import RejectionField from '@/src/app/components/dialog/RejectionField';
import DropdownStyled from '@/src/app/components/inputs/StyledInputs/DropdownStyled';
import ApprovalHistory from '@/src/app/components/approvals/ApprovalHistory';
import { useRolesAccessContext } from '../../../context/RolesAccessContext';
import { APEntryDraftSummary } from '../../../Interfaces/Accounting/ACForms.interfaces';
import APEntrySuccess from '@/src/app/features/accounting/forms/APTransactionEntry/APEntrySuccess';
import APEntryParams from '@/src/app/features/accounting/forms/APTransactionEntry/APEntryParams';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '@/src/app/components/messages/ErrorMessage';

const draftLabel = (draft: APEntryDraftSummary) =>
  [formatUTCDate(draft.updatedAt || ''), draft.number, draft.userName]
    .filter((value) => value)
    .join(' - ');

const APTransactionEntry = () => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const formMethods = useForm<APTransactionEntryForm>();
  const [draftID, setDraftID] = useState<string>();
  const [selectedID, setSelectedID] = useState<string>();
  const [submittedID, setSubmittedID] = useState<string>();
  const [showHistory, setShowHistory] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { handleSubmit, getValues, control, formState } = formMethods;
  const fieldArray = useFieldArray<APTransactionEntryForm>({
    control,
    name: 'items',
  });
  const { replace } = fieldArray;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useRef<Toast>(null);
  const prevSelectedCompanyRef = useRef<string | null>(null);

  const access = rolesAcess?.find(
    (access) => access.report === 'ap_transaction_entry'
  );
  const approvalMode = !!submittedID;
  const ownDraftSelected = !!draftID && !approvalMode;

  const entryID = submittedID ?? selectedID;

  const activeAPEntryRequest = useQuery({
    queryKey: ['getActiveAPEntry', selectedCompany, entryID],
    queryFn: ({ signal }) =>
      entryID ? fetchAPEntryById(entryID, signal) : Promise.resolve(null),
    onSuccess: (data) => {
      setSubmitDisabled(!!data?.submitted);
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && !!entryID,
    cacheTime: 0,
  });

  const draftsRequest = useQuery({
    queryKey: ['getAPEntryDrafts', selectedCompany],
    queryFn: ({ signal }) => fetchAPEntryDrafts(selectedCompany!.id, signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
    cacheTime: 0,
  });

  const submittedEntriesRequest = useQuery({
    queryKey: ['getSubmittedAPEntries', selectedCompany],
    queryFn: ({ signal }) =>
      fetchSubmittedAPEntries(selectedCompany!.id, signal),
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && access?.shouldApprove),
    staleTime: 1000 * 60 * 10,
    cacheTime: 0,
  });

  const saveRequest = useMutation({
    mutationFn: (
      data: APTransactionEntryArgs | APTransactionEntryUpdateArgs
    ) => {
      if (draftID) {
        return putAPTransactionEntry(
          draftID,
          data as APTransactionEntryUpdateArgs
        );
      }

      return postAPTransactionEntry(data as APTransactionEntryArgs);
    },
    onSuccess: (data) => {
      setSubmitDisabled(false);
      if (!draftID) {
        setDraftID(data.id);
      }

      draftsRequest.refetch();

      formMethods.reset(
        {
          ...formMethods.getValues(),
          prevAttachment: data.attachmentName,
          attachment: undefined,
        },
        { keepDirty: false }
      );

      showToast(
        'success',
        toast,
        'Save AP Transaction Entry',
        'The data was saved successfully',
        10000
      );
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'AP Transaction Entry Save Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      return putSubmitAPTransactionEntry(draftID ?? '');
    },
    onSuccess: (data) => {
      setSubmitDisabled(true);

      showToast(
        'success',
        toast,
        'Submit AP Transaction Entry',
        'The data was submitted successfully',
        10000
      );
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'AP Transaction Entry Submit Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const uploadRequest = useMutation({
    mutationFn: () => putUploadAPTransactionEntry(draftID ?? ''),
    onSuccess: () => {
      showToast(
        'success',
        toast,
        'Upload AP Transaction Entry',
        'The draft was uploaded successfully!',
        3000
      );
    },
    onError: (error: AxiosError) => {
      const data = error.response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Upload AP Transaction Entry',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const approveRequest = useMutation({
    mutationFn: () => putApproveAPTransactionEntry(submittedID ?? ''),
    onSuccess: () => {
      showToast(
        'success',
        toast,
        'Approve AP Transaction Entry',
        'The draft was approved successfully!',
        3000
      );
    },
    onError: (error: AxiosError) => {
      const data = error.response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Approve AP Transaction Entry',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const deleteRequest = useMutation({
    mutationFn: () => deleteAPTransactionEntry(draftID ?? ''),
    onSuccess: () => {
      selectDraft(undefined);
      draftsRequest.refetch();

      showToast(
        'success',
        toast,
        'Delete AP Transaction Entry',
        'The draft was deleted successfully!',
        3000
      );
    },
    onError: (error: AxiosError) => {
      const data = error.response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Delete AP Transaction Entry',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const rejectRequest = useMutation({
    mutationFn: (reason: string) =>
      putRejectAPTransactionEntry(submittedID ?? '', reason),
    onSuccess: () => {
      showToast(
        'success',
        toast,
        'Reject AP Transaction Entry',
        'The draft was rejected successfully!',
        3000
      );
    },
    onError: (error: AxiosError) => {
      const data = error.response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Reject AP Transaction Entry',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const resetActions = () => {
    setSubmitDisabled(false);
    approveRequest.reset();
    rejectRequest.reset();
    uploadRequest.reset();
    deleteRequest.reset();
  };

  const selectDraft = (id?: string) => {
    setDraftID(id);
    setSelectedID(id);
    setSubmittedID(undefined);
    resetActions();

    if (!id) {
      resetDraftCB();
      if (access?.shouldApprove) submittedEntriesRequest.refetch();
    }
  };

  const selectSubmitted = (id?: string) => {
    setSubmittedID(id);
    setDraftID(undefined);
    setSelectedID(undefined);
    resetActions();

    if (!id) {
      resetDraftCB();
      draftsRequest.refetch();
    }
  };

  const onSave = () => {
    const data = getValues();
    const {
      vendor,
      description,
      number,
      items,
      date,
      attachment,
      removeAttachment,
    } = data;
    const basePayload = {
      vendor,
      description: description || undefined,
      number: number || undefined,
      date,
      items:
        items
          ?.filter((item) => {
            if (item.type === APTransactionItemType.gl)
              return item.glAcct || item.total || item.description;
            return item.job || item.total || item.description || item.um;
          })
          .map((item) => {
            const baseItem = {
              description: item.description || undefined,
              total: item.total ?? undefined,
            };

            if (item.type === APTransactionItemType.gl) {
              return { ...baseItem, glAcct: item.glAcct ?? undefined };
            }

            return {
              ...baseItem,
              phase: item.phase ?? undefined,
              costType: item.costType ?? undefined,
              job: item.job ?? undefined,
              um: item.um ?? undefined,
              units: item.um !== 'LS' ? item.units ?? undefined : undefined,
              price: item.um !== 'LS' ? item.price ?? undefined : undefined,
              total:
                !item.um || item.um === 'LS'
                  ? item.total ?? undefined
                  : undefined,
            };
          }) ?? undefined,
      attachment: attachment ?? undefined,
    };

    if (!draftID) {
      const payload = {
        ...basePayload,
        companyID: selectedCompany!.id,
      };

      saveRequest.mutate(payload);
    } else {
      const payload = {
        ...basePayload,
        removeAttachment,
        fromApproval: approvalMode,
      };

      saveRequest.mutate(payload);
    }
  };

  const validateEntry = (data: APTransactionEntryForm): boolean => {
    const items = data.items ?? [];
    const total = data.total ?? 0;

    if (!items.length) {
      showToast(
        'error',
        toast,
        'AP Transaction Entry Error',
        `The invoice must have at least one item!`,
        3000
      );
      return false;
    }

    const itemsTotal =
      items.reduce((sum, item) => sum + (item.total ?? 0), 0) ?? 0;
    if (roundCurrency(itemsTotal) !== total && total !== 0) {
      showToast(
        'error',
        toast,
        'AP Transaction Entry Error',
        `Items total must match Invoice Total. The total is ${
          itemsTotal > total ? 'lower' : 'higher'
        } than the total items amount!`,
        3000
      );
      return false;
    }

    return true;
  };

  const onSubmit = (data: APTransactionEntryForm) => {
    if (validateEntry(data)) mutate();
  };

  const onUpload = (data: APTransactionEntryForm) => {
    if (validateEntry(data)) uploadRequest.mutate();
  };

  const resetDraft = (removePrevAttachment = false) => {
    approveRequest.reset();
    uploadRequest.reset();
    rejectRequest.reset();
    formMethods.reset(
      {
        vendorType: undefined,
        vendor: undefined,
        number: undefined,
        date: undefined,
        description: undefined,
        total: undefined,
        items: [],
        attachment: undefined,
        removeAttachment: removePrevAttachment && !!getValues('prevAttachment'),
      },
      { keepDefaultValues: false }
    );
    replace([]);
  };
  const resetDraftCB = useCallback(resetDraft, [
    approveRequest.reset,
    uploadRequest.reset,
    rejectRequest.reset,
    formMethods,
    replace,
    getValues,
  ]);

  useEffect(() => {
    if (prevSelectedCompanyRef.current === null) {
      prevSelectedCompanyRef.current = selectedCompany?.id || null;
      return;
    }

    if (prevSelectedCompanyRef.current === selectedCompany?.id) return;

    prevSelectedCompanyRef.current = selectedCompany?.id || null;

    if (searchParams.get('company')) return;

    setSubmittedID(undefined);
    setDraftID(undefined);
    setSelectedID(undefined);
    resetDraftCB();
  }, [selectedCompany, resetDraftCB, searchParams]);

  useEffect(() => {
    if (activeAPEntryRequest.data && activeAPEntryRequest.isSuccess) {
      const invoice = activeAPEntryRequest.data;
      formMethods.reset({
        vendor: invoice.vendor,
        vendorType: invoice.vendorType,
        number: invoice.number,
        description: invoice.description,
        date: invoice.date ? new Date(invoice.date) : undefined,
        total: invoice.total,
        items: (invoice.items ?? []).map((item) => {
          if ('job' in item) {
            return {
              ...item,
              type: APTransactionItemType.jc,
              price: item.price ?? 0,
              units: item.units ?? 0,
            };
          }

          return { ...item, type: APTransactionItemType.gl };
        }),
        prevAttachment: invoice.attachmentName ?? undefined,
      });
      setDraftID(invoice.id);

      const t = setTimeout(() => {
        formMethods.reset(formMethods.getValues());
      }, 0);
      return () => clearTimeout(t);
    } else if (activeAPEntryRequest.isSuccess) {
      resetDraftCB();
    }

    return;
  }, [
    activeAPEntryRequest.data,
    activeAPEntryRequest.isSuccess,
    formMethods,
    setDraftID,
    resetDraftCB,
  ]);

  if (activeAPEntryRequest.isFetching) {
    return (
      <div className="flex flex-column justify-content-center gap-5 pt-4">
        <div className="text-35px font-bold text-center mb-2 relative">
          New AP Transaction Entry
        </div>
        <div className="text-center mx-auto mt-3">
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (activeAPEntryRequest.isError) {
    return (
      <div className="flex flex-column justify-content-center gap-5 pt-4">
        <div className="text-35px font-bold text-center mb-2 relative">
          New AP Transaction Entry
        </div>
        <ErrorMessage
          content={'Failed to obtain data! Please try again later.'}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <APEntryParams onSubmittedID={selectSubmitted} onDraftID={selectDraft} />
      <div className="text-35px font-bold text-center mb-2 relative">
        <div>New AP Transaction Entry</div>
        {ownDraftSelected && (
          <div className="flex justify-content-center lg:absolute top-0 right-0 mt-4 lg:mt-0 lg:mr-8">
            <ConfirmationDialog
              tagKey="draft-delete"
              Button={
                <LoadingButton
                  isLoading={deleteRequest.isPending}
                  label="Delete"
                  fontSize="text-22px"
                  className="w-15rem bluwaiRed border-0 flex py-3"
                  type="button"
                  disabled={deleteRequest.isPending}
                />
              }
              onConfirm={() => deleteRequest.mutate()}
              message="Are you sure you want to delete this AP transaction entry?"
            />
          </div>
        )}
      </div>
      <div className="max-w-600px w-full px-2 sm:px-0 mx-auto flex gap-4 justify-content-between flex-wrap sm:flex-nowrap">
        {access?.editable && !approvalMode && (
          <div className="w-full">
            <label htmlFor="draftID" className="text-standard block mb-2">
              Active Drafts
            </label>
            <DropdownStyled
              id="draftID"
              value={draftID}
              options={
                draftsRequest.data?.map((draft: APEntryDraftSummary) => ({
                  ...draft,
                  label: draftLabel(draft),
                })) || []
              }
              labelField="label"
              valueField="id"
              onChange={(e) => selectDraft(e.value ?? undefined)}
              placeholder={
                draftsRequest.isError
                  ? 'Failed to load active drafts!'
                  : 'Select Active Entry'
              }
              isDisabled={draftsRequest.isError || !draftsRequest.data?.length}
              isLoading={draftsRequest.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
            />
          </div>
        )}
        {access?.shouldApprove && !ownDraftSelected && (
          <div className="w-full">
            <label htmlFor="submittedID" className="text-standard block mb-2">
              Approve Submitted Drafts
            </label>
            <DropdownStyled
              id="submittedID"
              value={submittedID}
              options={
                submittedEntriesRequest.data?.map(
                  (draft: APEntryDraftSummary) => ({
                    ...draft,
                    label: draftLabel(draft),
                  })
                ) || []
              }
              labelField="label"
              valueField="id"
              onChange={(e) => selectSubmitted(e.value ?? undefined)}
              placeholder={
                submittedEntriesRequest.isError
                  ? 'Failed to load submitted drafts!'
                  : 'Approve Submitted Drafts'
              }
              isDisabled={
                submittedEntriesRequest.isError ||
                !submittedEntriesRequest.data?.length
              }
              isLoading={submittedEntriesRequest.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
            />
          </div>
        )}
        {activeAPEntryRequest.data?.approvals && (
          <div className="w-full flex justify-content-end min-fit">
            <div
              className="text-18px font-bold cursor-pointer totalColor hover:underline flex"
              onClick={() => setShowHistory(true)}
            >
              Check Draft Approvals
            </div>
            <ApprovalHistory
              show={showHistory}
              hide={() => setShowHistory(false)}
              approvals={activeAPEntryRequest.data?.approvals ?? []}
              lastStep={activeAPEntryRequest.data?.lastStep ?? 0}
            />
          </div>
        )}
      </div>
      <FormProvider {...formMethods}>
        {!(approveRequest.isSuccess || uploadRequest.isSuccess) && (
          <form className={`max-w-600px w-full mx-auto flex flex-column gap-5`}>
            <APTransactionForm fieldArray={fieldArray} draftID={draftID} />
            <div className="flex flex-wrap gap-2 row-gap-4 mb-3 mt-5">
              <ConfirmationDialog
                tagKey="draft-clear"
                Button={
                  <LoadingButton
                    label="Clear Form"
                    fontSize="text-22px"
                    isLoading={false}
                    className="w-15rem bg-orange-500 border-0 flex m-auto py-3 flex-order-2 sm:flex-order-0"
                    type="button"
                    disabled={
                      approveRequest.isSuccess ||
                      uploadRequest.isSuccess ||
                      rejectRequest.isSuccess
                    }
                  />
                }
                onConfirm={() => {
                  resetDraftCB(true);
                }}
                message={'Are you sure you want to clear the form data?'}
              />
              <ConfirmationDialog
                tagKey="draft-save"
                Button={
                  <LoadingButton
                    isLoading={saveRequest.isPending}
                    label="Save"
                    className="w-15rem buttonSecondary border-0 flex m-auto py-3 flex-order-0 sm:flex-order-0"
                    fontSize="text-22px"
                    type="button"
                    disabled={!formState.isDirty || rejectRequest.isSuccess}
                  />
                }
                onConfirm={onSave}
                message="Are you sure you want to save the AP transaction entry?"
              />
              <Button
                onClick={() => {
                  navigate('/accounting-dashboard/new');
                }}
                className="w-15rem bluwaiRed border-0 flex m-auto py-3 flex-order-3 sm:flex-order-0"
                type="button"
              >
                <span className="mx-auto text-22px text-white">Cancel</span>
              </Button>
              {!approvalMode && access?.editable === 'approval' && (
                <ConfirmationDialog
                  tagKey="draft-submit"
                  Button={
                    <LoadingButton
                      isLoading={isPending}
                      label="Submit"
                      className="w-15rem bluwaiBlue border-0 flex m-auto py-3 flex-order-1 sm:flex-order-0"
                      fontSize="text-22px"
                      type="button"
                      disabled={!draftID || formState.isDirty || submitDisabled}
                    />
                  }
                  onConfirm={handleSubmit(onSubmit)}
                  message="Are you sure you want to submit the AP transaction entry?"
                />
              )}
              {!approvalMode && access?.editable === 'no-approval' && (
                <ConfirmationDialog
                  tagKey="draft-upload"
                  Button={
                    <LoadingButton
                      isLoading={uploadRequest.isPending}
                      label="Upload"
                      className="w-15rem bg-green-500 border-0 flex m-auto py-3 flex-order-1 sm:flex-order-0"
                      fontSize="text-22px"
                      type="button"
                      disabled={
                        !draftID || formState.isDirty || uploadRequest.isPending
                      }
                    />
                  }
                  onConfirm={handleSubmit(onUpload)}
                  message="Are you sure you want to upload the AP transaction entry?"
                />
              )}
              {approvalMode && access?.shouldApprove && (
                <ConfirmationDialog
                  tagKey="draft-rejection"
                  visibility={visibility}
                  Button={
                    <LoadingButton
                      label="Reject"
                      fontSize="text-22px"
                      className="w-15rem bluwaiRed border-0 flex m-auto py-3 flex-order-1 sm:flex-order-0"
                      isLoading={rejectRequest.isPending}
                      disabled={
                        formState.isDirty ||
                        rejectRequest.isPending ||
                        rejectRequest.isSuccess
                      }
                      type="button"
                      onClick={() => setVisibility(true)}
                    />
                  }
                  contentClassName="pb-0"
                  onConfirm={() => undefined}
                  acceptClassName="hidden"
                  rejectClassName="hidden"
                  onHide={() => setVisibility(false)}
                  message={
                    <RejectionField
                      onReject={() => {
                        setVisibility(false);
                      }}
                      onConfirm={(reason) => {
                        rejectRequest.mutate(reason);
                        setVisibility(false);
                      }}
                    />
                  }
                />
              )}
              {approvalMode && access?.shouldApprove && (
                <ConfirmationDialog
                  tagKey="draft-approval"
                  Button={
                    <LoadingButton
                      label="Approve"
                      fontSize="text-22px"
                      className="w-15rem bg-green-500 border-0 flex m-auto py-3 flex-order-1 sm:flex-order-0"
                      isLoading={approveRequest.isPending}
                      disabled={
                        formState.isDirty ||
                        approveRequest.isPending ||
                        rejectRequest.isSuccess
                      }
                      type="button"
                    />
                  }
                  onConfirm={() => {
                    approveRequest.mutate();
                  }}
                  message="Are you sure you want to approve this draft?"
                />
              )}
            </div>
          </form>
        )}
        {(approveRequest.isSuccess || uploadRequest.isSuccess) && (
          <APEntrySuccess />
        )}
      </FormProvider>
      <ErrorToast toastRef={toast} />
    </div>
  );
};

export default APTransactionEntry;
